import { Button, message, Spin } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import DOMPurify from 'dompurify'
import { getAsset3DModelImageURL } from 'features/assetDatabase'
import { generateAssetModel } from 'features/assetModeling'
import { useRolodexEdit } from 'features/fde'
import { useState } from 'react'
import styled from 'styled-components'
import { GeckoAssetModelType, GeckoUnit, getAssetModelTypeName } from 'types'

const StyledDescription = styled.div`
  display: flex;
  padding-bottom: 0.938rem;
  flex-direction: column;
  height: 12.5rem;
  justify-content: space-around;
`

const StyledUnitTypeButtons = styled(Button)`
  width: 100%;
  height: 3.125rem;
  justify-content: start;
  padding: 0 0.625rem 0 0.625rem;
  display: flex;
  margin-bottom: 0.625rem;

  img {
    max-width: 2.5rem;
    max-height: 2.5rem;
    object-fit: contain;
    margin-right: 0.625rem;
  }
`

const StyledSpinDiv = styled.div`
  display: flex;
  justify-content: center;
`

interface AddUnitRichAssetModelInterface {
  unit: GeckoUnit
  refreshData: () => void
  bypassRAMCreation?: (value: boolean) => void
  goToSelectComponent?: () => void
}

export const AddUnitRichAssetModel = ({
  unit,
  refreshData,
  bypassRAMCreation,
  goToSelectComponent,
}: AddUnitRichAssetModelInterface) => {
  const { rolodexPut } = useRolodexEdit()
  const { GECKO_ASSETS_URL } = useRuntimeVariables()
  const [messageApi, contextHolder] = message.useMessage()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const sanitizedURL = DOMPurify.sanitize(GECKO_ASSETS_URL)

  const handleUnitTypeClick = (
    unit: GeckoUnit,
    assetModelType: GeckoAssetModelType
  ) => {
    setIsLoading(true)
    const newAssetModel = generateAssetModel(unit, assetModelType)
    rolodexPut([newAssetModel])
      .then(() => {
        refreshData()
        return messageApi.success(
          'Successfully created a new Rich Asset Model! Loading...'
        )
      })
      .then(() => {
        setIsLoading(false)
        if (bypassRAMCreation) bypassRAMCreation(false)
        if (goToSelectComponent) goToSelectComponent()
      })
      .catch(() => {
        messageApi.error(
          'Failed to create new Rich Asset Model. Please try again.'
        )
      })
  }

  return (
    <div>
      {contextHolder}
      <StyledDescription>
        <h4>Create a Rich Asset Model</h4>
        <p>
          Before you can edit components on this unit, we need to know what kind
          of unit this is. This allows us to create a rich asset model, and
          improve the user experience.
        </p>
        {!!bypassRAMCreation && (
          <p>
            <strong>Note:</strong> If you don&apos;t see an appropriate unit
            type below, please skip this step by following the instructions
            below the selection.
          </p>
        )}
      </StyledDescription>
      <div>
        <div>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.VerticalTank)
            }
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.VerticalTank,
                sanitizedURL
              )}
              alt={'Icon image of a large vertical tank'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.VerticalTank)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.HorizontalTank)
            }
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.HorizontalTank,
                sanitizedURL
              )}
              alt={'Icon image of a horizontal tank'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.HorizontalTank)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.Piping)
            }
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.Piping,
                sanitizedURL
              )}
              alt={'Icon image of a long pipe'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.Piping)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.LegacyBoiler)
            }
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.LegacyBoiler,
                sanitizedURL
              )}
              alt={'Icon image of a power boiler'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.LegacyBoiler)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() => handleUnitTypeClick(unit, GeckoAssetModelType.Ship)}
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.Ship,
                sanitizedURL
              )}
              alt={'Icon image of a large ship'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.Ship)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.MissileSilo)
            }
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.MissileSilo,
                sanitizedURL
              )}
              alt={'Icon image of a missile silo'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.MissileSilo)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(
                unit,
                GeckoAssetModelType.NuclearContainmentDome
              )
            }
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.NuclearContainmentDome,
                sanitizedURL
              )}
              alt={'Icon image of a nuclear containment dome'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.NuclearContainmentDome)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.MachinedForging)
            }
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.MachinedForging,
                sanitizedURL
              )}
              alt={'Icon image of a machined forging'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.MachinedForging)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.PressureVessel)
            }>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.PressureVessel,
                sanitizedURL
              )}
              alt={'Icon image of a pressure vessel'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.PressureVessel)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() => handleUnitTypeClick(unit, GeckoAssetModelType.Hood)}
            disabled={isLoading}>
            <img
              src={getAsset3DModelImageURL(
                GeckoAssetModelType.Hood,
                sanitizedURL
              )}
              alt={'Icon image of a hood'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.Hood)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() => handleUnitTypeClick(unit, GeckoAssetModelType.Fan)}
            disabled={isLoading}>
            {getAssetModelTypeName(GeckoAssetModelType.Fan)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.Blower)
            }
            disabled={isLoading}>
            {getAssetModelTypeName(GeckoAssetModelType.Blower)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() => handleUnitTypeClick(unit, GeckoAssetModelType.Pump)}
            disabled={isLoading}>
            {getAssetModelTypeName(GeckoAssetModelType.Pump)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() => handleUnitTypeClick(unit, GeckoAssetModelType.Motor)}
            disabled={isLoading}>
            {getAssetModelTypeName(GeckoAssetModelType.Motor)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() => handleUnitTypeClick(unit, GeckoAssetModelType.Valve)}
            disabled={isLoading}>
            {getAssetModelTypeName(GeckoAssetModelType.Valve)}
          </StyledUnitTypeButtons>
        </div>
        {!!bypassRAMCreation && (
          <p>
            Not ready to make a Rich Asset Model for this Unit?{' '}
            <a
              onClick={() => {
                if (bypassRAMCreation) bypassRAMCreation(true)
                if (goToSelectComponent) goToSelectComponent()
              }}>
              {' '}
              Edit the components directly here
            </a>
          </p>
        )}
      </div>
      <StyledSpinDiv>
        <Spin spinning={isLoading} />
      </StyledSpinDiv>
    </div>
  )
}
